import { createApp, markRaw } from 'vue';
import AppComponent from '@/components/AppComponent.vue';
import { useRouter } from '@/router';
import { createPinia } from 'pinia';
import { PiniaDebounce } from '@pinia/plugin-debounce';
import { debounce, isEmbedded } from '@/helpers';
import type { Router } from 'vue-router';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createGtag, type Pageview } from 'vue-gtag';
import { initSentry } from '@/services/sentryService';
import { registerStaleAssetRecovery } from '@/services/staleAssetService';
import '@/styles/global.scss';

const router = useRouter();

// Not sure why we have to put this here instead of in our vite-end.d.ts file
declare module 'pinia' {
	export interface MapStoresCustomization {
		suffix: '';
	}

	export interface PiniaCustomProperties {
		$router: Router;
	}
}

const app = createApp(AppComponent);

registerStaleAssetRecovery();
initSentry(app);

const piniaInstance = createPinia();
piniaInstance.use(PiniaDebounce(debounce));
piniaInstance.use(({ store }) => {
	store.$router = markRaw(router);
});
piniaInstance.use(piniaPluginPersistedstate);

// The order of here is important because we use pinia in the routing on loadc
app.use(piniaInstance);
app.use(router);
app.directive('embed-hide', {
	mounted(el) {
		if (isEmbedded) {
			el.style.display = 'none';
		}
	},
	updated(el) {
		if (isEmbedded) {
			el.style.display = 'none';
		}
	},
});

app.directive('embed-show', {
	mounted(el) {
		if (!isEmbedded) {
			el.style.display = 'none';
		}
	},
	updated(el) {
		if (!isEmbedded) {
			el.style.display = 'none';
		}
	},
});

app.use(
	createGtag({
		tagId: import.meta.env.VITE_GOOGLE_ANALYTICS_GA_ID,
		pageTracker: {
			router,
			template: (to) => {
				return {
					page_title: to.name ? to.name.toString() : 'Unknown',
					page_path: to.path,
				} as Pageview;
			},
		},
	})
);

app.mount('#app');
